import React, { useState } from 'react'
import { Icons } from '@pankod/refine-antd'
import {
  useCan,
  useCreate,
  useNavigation,
  useOne,
  useUpdate,
} from '@pankod/refine-core'
import { useRouter } from 'next/router'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { CertificateSettingsDrawer } from '../../../../program-setup/blocks/DrawerCertificateSettings'
import ResetSertifikatModal, {
  useResetSertifikatModal,
} from '../../../../program-setup/blocks/reset-sertifikat/ResetSertifikatModal'
import MenuItemPopconfirm from '../../MenuItemPopconfirm'
import { Highlighter } from '../../utils'
import { StatusTag } from '../StatusTag'

import type {
  TConfigurationSection,
  TConfigurationSectionContext,
} from '../types'

export const useCertificateConfiguration = ({
  isOwner,
  program,
  programId,
  runAndClose,
  search,
}: TConfigurationSectionContext): TConfigurationSection => {
  const router = useRouter()
  const { listUrl } = useNavigation()
  const [settingsVisible, setSettingsVisible] = useState(false)
  const { mutateAsync: resetCertificate } = useCreate()
  const {
    mutate: toggleCertificateApproval,
    isLoading: isLoadingCertificateApproval,
  } = useUpdate()
  const { show: showResetModal, ...resetModalProps } = useResetSertifikatModal()
  const { data: toggleCertificateData } = useOne<{
    data: { active: boolean }
  }>({
    dataProviderName: 'lms',
    id: 'certificate-toggle',
    resource: `programs/${programId}`,
  })
  const { data: canResetCertificate } = useCan({
    action: 'show',
    resource: 'reset-certificate-program',
  })
  const { data: canDesignTemplates } = useCan({
    action: 'show',
    resource: 'design-certificate-templates',
  })
  const { data: canToggleApproval } = useCan({
    action: 'show',
    resource: 'toggle-certificate-approval',
  })
  const approvalActive = toggleCertificateData?.data?.data?.active
  const useNewCertificate = program?.useNewCertificate

  return {
    menuItems: [
      {
        type: 'group',
        label: <Highlighter search={search} text="Sertifikat" />,
        key: 'Sertifikat',
        children: [
          ...(canDesignTemplates?.can || isOwner
            ? [
                {
                  icon: <Icons.PictureOutlined />,
                  label: (
                    <Highlighter
                      search={search}
                      text="Kelola Design Template"
                    />
                  ),
                  key: 'Kelola Design Template',
                  onClick: () =>
                    runAndClose(() => {
                      const url = listUrl('certificate-templates')
                      return router.push(`${url}?programId=${programId}`)
                    }),
                },
              ]
            : []),
          {
            icon: <Icons.FileDoneOutlined />,
            label: <Highlighter search={search} text="Pengaturan Sertifikat" />,
            key: 'Pengaturan Sertifikat',
            onClick: () => runAndClose(() => setSettingsVisible(true)),
            disabled: useNewCertificate,
          },
          ...(canResetCertificate?.can || isOwner
            ? [
                {
                  icon: <Icons.FileSyncOutlined />,
                  danger: true,
                  label: useNewCertificate ? (
                    <Highlighter search={search} text="Reset Sertifikat" />
                  ) : (
                    <MenuItemPopconfirm
                      title="Apakah Anda yakin ingin melakukan reset seluruh sertifikat pada program ini?"
                      onConfirm={() =>
                        resetCertificate({
                          dataProviderName: 'lms',
                          values: {},
                          resource: `v2/programs/${programId}/reset-certificate`,
                          successNotification: {
                            message: 'Sertifikat berhasil direset',
                            type: 'success',
                            description: 'Sukses',
                          },
                          errorNotification: (error) =>
                            showErrorNotification(
                              error,
                              'Terdapat gangguan saat reset sertifikat',
                            ),
                        })
                      }
                      placement="right"
                      okText="Reset"
                      okButtonProps={{ danger: true }}
                      cancelText="Batal"
                    >
                      <Highlighter search={search} text="Reset Sertifikat" />
                    </MenuItemPopconfirm>
                  ),
                  key: 'Reset Sertifikat',
                  onClick: () => {
                    if (!useNewCertificate) return
                    runAndClose(() => showResetModal(programId))
                  },
                },
              ]
            : []),
          ...(canToggleApproval?.can || isOwner
            ? [
                {
                  key: 'Pengabsahan',
                  type: 'group' as const,
                  label: (
                    <div className="flex justify-between gap-2">
                      <Highlighter search={search} text="Pengabsahan" />
                      <StatusTag
                        active={!!approvalActive}
                        activeText="AKTIF"
                        inactiveText="NONAKTIF"
                      />
                    </div>
                  ),
                  children: [
                    {
                      icon: isLoadingCertificateApproval ? (
                        <Icons.LoadingOutlined spin />
                      ) : approvalActive ? (
                        <Icons.StopOutlined />
                      ) : (
                        <Icons.FileProtectOutlined />
                      ),
                      key: approvalActive
                        ? 'Nonaktifkan Pengabsahan (Approval)'
                        : 'Aktifkan Pengabsahan (Approval)',
                      danger: approvalActive,
                      disabled: isLoadingCertificateApproval,
                      label: (
                        <MenuItemPopconfirm
                          title={`Apakah Anda ingin ${
                            approvalActive ? 'me-nonaktifkan' : 'mengaktifkan'
                          } pengabsahan (approval) sertifikat?`}
                          okText={approvalActive ? 'Nonaktifkan' : 'Aktifkan'}
                          cancelText="Batal"
                          disabled={isLoadingCertificateApproval}
                          okButtonProps={{ danger: approvalActive }}
                          onConfirm={() =>
                            toggleCertificateApproval({
                              resource: `programs/${programId}`,
                              dataProviderName: 'lms',
                              id: 'certificate-toggle',
                              values: { active: !approvalActive },
                              successNotification: {
                                message:
                                  'Pengabsahan sertifikat berhasil dikonfigurasi ulang',
                                type: 'success',
                                description: 'Sukses',
                              },
                              errorNotification: (error) =>
                                showErrorNotification(
                                  error,
                                  'Terdapat gangguan saat mengubah konfigurasi pengabsahan sertifikat',
                                ),
                            })
                          }
                        >
                          <Highlighter
                            search={search}
                            text={
                              approvalActive
                                ? 'Nonaktifkan Pengabsahan (Approval)'
                                : 'Aktifkan Pengabsahan (Approval)'
                            }
                          />
                        </MenuItemPopconfirm>
                      ),
                    },
                  ],
                },
              ]
            : []),
        ],
      },
    ],
    overlays: (
      <>
        <CertificateSettingsDrawer
          programId={programId}
          programName={program?.name}
          visible={settingsVisible}
          onClose={() => setSettingsVisible(false)}
        />
        <ResetSertifikatModal {...resetModalProps} />
      </>
    ),
  }
}
